import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Package, CheckCircle, Clock, Truck, MapPin, Star, Phone, AlertCircle, ArrowRight, RefreshCw } from 'lucide-react';
import PublicNavigation from '../../components/ui/PublicNavigation';
import UnifiedFooter from '../../components/ui/UnifiedFooter';
import { supabase } from '../../lib/supabase';

const STEPS = [
  {
    key: 'confirmed',
    label: 'Confirmée',
    description: 'Votre commande a été reçue et confirmée',
    icon: CheckCircle,
    color: 'text-emerald-500',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    activeBg: 'bg-emerald-500',
  },
  {
    key: 'prepared',
    label: 'Préparée',
    description: 'Votre commande est en cours de préparation',
    icon: Package,
    color: 'text-blue-500',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    activeBg: 'bg-blue-500',
  },
  {
    key: 'collected',
    label: 'En collecte',
    description: 'Votre commande est en cours de collecte',
    icon: MapPin,
    color: 'text-amber-500',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    activeBg: 'bg-amber-500',
  },
  {
    key: 'shipped',
    label: 'Expédiée',
    description: 'Votre commande est en route vers vous',
    icon: Truck,
    color: 'text-purple-500',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    activeBg: 'bg-purple-500',
  },
  {
    key: 'delivered',
    label: 'Livrée',
    description: 'Votre commande a été livrée avec succès',
    icon: Star,
    color: 'text-rose-500',
    bg: 'bg-rose-50',
    border: 'border-rose-200',
    activeBg: 'bg-rose-500',
  },
];

const BARREL_TYPE_LABELS = {
  noir_270l: 'Barrique Noire 270L',
  bleu_220l: 'Barrique Bleue 220L',
};

const STATUS_TO_STEP = {
  pending: 0,
  confirmed: 1,
  delivered: 4,
  cancelled: -1,
};

function getActiveStepIndex(order) {
  if (!order) return -1;
  const status = order?.order_status || order?.status || 'pending';
  if (status === 'cancelled') return -1;
  if (status === 'delivered') return 4;
  if (status === 'confirmed') {
    // Estimate progress based on delivery_date
    const now = new Date();
    const deliveryDate = order?.delivery_date ? new Date(order.delivery_date) : null;
    const createdAt = order?.created_at ? new Date(order.created_at) : null;

    if (deliveryDate && createdAt) {
      const totalDuration = deliveryDate - createdAt;
      const elapsed = now - createdAt;
      const progress = Math.min(elapsed / totalDuration, 1);

      if (progress >= 0.85) return 3; // shipped
      if (progress >= 0.55) return 2; // collected
      if (progress >= 0.2) return 1;  // prepared
    }
    return 1; // confirmed
  }
  return 0; // pending
}

function getStepTimestamp(order, stepIndex, activeIndex) {
  if (!order || stepIndex > activeIndex) return null;

  const createdAt = order?.created_at ? new Date(order.created_at) : null;
  const updatedAt = order?.updated_at ? new Date(order.updated_at) : null;
  const deliveryDate = order?.delivery_date ? new Date(order.delivery_date) : null;

  if (stepIndex === 0) return createdAt; // confirmed = order creation
  if (stepIndex === 4 && order?.order_status === 'delivered') return updatedAt;
  if (stepIndex === activeIndex && stepIndex > 0 && stepIndex < 4) return updatedAt;

  // Interpolate timestamps for intermediate steps
  if (createdAt && (updatedAt || deliveryDate)) {
    const endDate = deliveryDate || updatedAt;
    const totalMs = endDate - createdAt;
    const fraction = stepIndex / 4;
    return new Date(createdAt.getTime() + totalMs * fraction);
  }

  return null;
}

function formatDate(date) {
  if (!date) return null;
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })?.format(date);
}

export default function BarrelOrderTracking() {
  const [searchValue, setSearchValue] = useState('');
  const [searchType, setSearchType] = useState('phone'); // 'phone' or 'id'
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!searchValue?.trim()) return;

    setLoading(true);
    setError('');
    setOrder(null);
    setSearched(true);

    try {
      let query = supabase?.from('barrel_sales')?.select('*')?.neq('order_status', 'cancelled');

      if (searchType === 'phone') {
        query = query?.ilike('customer_phone', `%${searchValue?.trim()}%`);
      } else {
        query = query?.eq('id', searchValue?.trim());
      }

      const { data, error: dbError } = await query?.order('created_at', { ascending: false })?.limit(1)?.single();

      if (dbError || !data) {
        setError('Aucune commande trouvée. Vérifiez votre numéro de téléphone ou identifiant de commande.');
      } else {
        setOrder(data);
      }
    } catch (err) {
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const activeStepIndex = getActiveStepIndex(order);
  const isCancelled = order?.order_status === 'cancelled' || order?.status === 'cancelled';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <PublicNavigation />
      <main className="flex-1 pt-20">
        {/* Hero */}
        <div className="bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900 text-white py-14 px-4">
          <div className="max-w-2xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 text-sm mb-5">
                <Package size={14} className="text-amber-400" />
                <span className="text-white/90">Suivi de commande barrique</span>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold mb-3 tracking-tight">
                Où en est ma commande ?
              </h1>
              <p className="text-white/70 text-base max-w-md mx-auto">
                Entrez votre numéro de téléphone pour suivre l'avancement de votre commande de barrique en temps réel.
              </p>
            </motion.div>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 py-10">
          {/* Search Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-8"
          >
            <form onSubmit={handleSearch} className="space-y-4">
              {/* Search type toggle */}
              <div className="flex gap-2 p-1 bg-gray-100 rounded-xl w-fit">
                <button
                  type="button"
                  onClick={() => setSearchType('phone')}
                  className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    searchType === 'phone' ?'bg-white text-slate-800 shadow-sm' :'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <span className="flex items-center gap-1.5">
                    <Phone size={13} />
                    Téléphone
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setSearchType('id')}
                  className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    searchType === 'id' ?'bg-white text-slate-800 shadow-sm' :'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <span className="flex items-center gap-1.5">
                    <Search size={13} />
                    N° commande
                  </span>
                </button>
              </div>

              <div className="flex gap-3">
                <input
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e?.target?.value)}
                  placeholder={
                    searchType === 'phone' ?'Ex: 06 12 34 56 78' :'Identifiant de commande'
                  }
                  className="flex-1 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-transparent placeholder-gray-400"
                />
                <button
                  type="submit"
                  disabled={loading || !searchValue?.trim()}
                  className="bg-slate-800 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-5 py-3 rounded-xl text-sm font-medium transition-colors flex items-center gap-2"
                >
                  {loading ? (
                    <RefreshCw size={15} className="animate-spin" />
                  ) : (
                    <Search size={15} />
                  )}
                  {loading ? 'Recherche...' : 'Suivre'}
                </button>
              </div>
            </form>
          </motion.div>

          {/* Error State */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="bg-red-50 border border-red-100 rounded-2xl p-5 mb-8 flex items-start gap-3"
              >
                <AlertCircle size={18} className="text-red-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-red-700 text-sm font-medium">Commande introuvable</p>
                  <p className="text-red-600 text-sm mt-0.5">{error}</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Order Result */}
          <AnimatePresence>
            {order && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="space-y-6"
              >
                {/* Order Summary Card */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h2 className="text-lg font-semibold text-slate-800">
                        Commande de {order?.customer_name}
                      </h2>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {BARREL_TYPE_LABELS?.[order?.barrel_type] || order?.barrel_type} — Qté : {order?.quantity}
                      </p>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      isCancelled
                        ? 'bg-red-100 text-red-700'
                        : order?.order_status === 'delivered' ?'bg-emerald-100 text-emerald-700' :'bg-amber-100 text-amber-700'
                    }`}>
                      {isCancelled ? 'Annulée' : order?.order_status === 'delivered' ? 'Livrée' : 'En cours'}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {order?.customer_phone && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Phone size={13} className="text-gray-400" />
                        {order?.customer_phone}
                      </div>
                    )}
                    {order?.delivery_date && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Clock size={13} className="text-gray-400" />
                        Livraison prévue : {new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })?.format(new Date(order.delivery_date))}
                      </div>
                    )}
                    {order?.customer_city && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <MapPin size={13} className="text-gray-400" />
                        {order?.customer_city}
                      </div>
                    )}
                    {order?.total_price && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <span className="text-gray-400 text-xs font-bold">€</span>
                        Total : {Number(order?.total_price)?.toFixed(2)} €
                      </div>
                    )}
                  </div>
                </div>

                {/* Cancelled State */}
                {isCancelled ? (
                  <div className="bg-red-50 border border-red-100 rounded-2xl p-6 text-center">
                    <AlertCircle size={32} className="text-red-400 mx-auto mb-3" />
                    <h3 className="text-red-700 font-semibold text-base mb-1">Commande annulée</h3>
                    <p className="text-red-600 text-sm">
                      Cette commande a été annulée. Contactez-nous pour plus d'informations.
                    </p>
                  </div>
                ) : (
                  /* Progress Timeline */
                  (<div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                    <h3 className="text-sm font-semibold text-slate-700 mb-6 uppercase tracking-wide">
                      Progression de la commande
                    </h3>
                    <div className="space-y-0">
                      {STEPS?.map((step, index) => {
                        const isCompleted = index <= activeStepIndex;
                        const isActive = index === activeStepIndex;
                        const isFuture = index > activeStepIndex;
                        const StepIcon = step?.icon;
                        const timestamp = getStepTimestamp(order, index, activeStepIndex);
                        const isLast = index === STEPS?.length - 1;

                        return (
                          <div key={step?.key} className="flex gap-4">
                            {/* Icon + connector */}
                            <div className="flex flex-col items-center">
                              <motion.div
                                initial={isActive ? { scale: 0.8 } : false}
                                animate={isActive ? { scale: [1, 1.08, 1] } : {}}
                                transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                                className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 border-2 transition-all ${
                                  isCompleted
                                    ? `${step?.activeBg} border-transparent`
                                    : 'bg-gray-100 border-gray-200'
                                } ${isActive ? 'ring-4 ring-offset-1 ring-opacity-30 ' + step?.activeBg?.replace('bg-', 'ring-') : ''}`}
                              >
                                <StepIcon
                                  size={18}
                                  className={isCompleted ? 'text-white' : 'text-gray-400'}
                                />
                              </motion.div>
                              {!isLast && (
                                <div className={`w-0.5 flex-1 my-1 min-h-[28px] rounded-full transition-all ${
                                  index < activeStepIndex ? step?.activeBg : 'bg-gray-200'
                                }`} />
                              )}
                            </div>
                            {/* Content */}
                            <div className={`pb-6 flex-1 ${isLast ? 'pb-0' : ''}`}>
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <p className={`font-semibold text-sm ${
                                    isCompleted ? 'text-slate-800' : 'text-gray-400'
                                  }`}>
                                    {step?.label}
                                    {isActive && (
                                      <span className="ml-2 inline-flex items-center gap-1 text-xs font-medium text-white bg-slate-700 px-2 py-0.5 rounded-full">
                                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                                        En cours
                                      </span>
                                    )}
                                  </p>
                                  <p className={`text-xs mt-0.5 ${
                                    isCompleted ? 'text-gray-500' : 'text-gray-300'
                                  }`}>
                                    {step?.description}
                                  </p>
                                </div>
                                {timestamp && isCompleted && (
                                  <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0 mt-0.5">
                                    {formatDate(timestamp)}
                                  </span>
                                )}
                                {isFuture && (
                                  <span className="text-xs text-gray-300 whitespace-nowrap flex-shrink-0 mt-0.5">
                                    À venir
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>)
                )}

                {/* Contact CTA */}
                <div className="bg-slate-50 border border-slate-100 rounded-2xl p-5 flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium text-slate-700">Une question sur votre commande ?</p>
                    <p className="text-xs text-gray-500 mt-0.5">Notre équipe est disponible pour vous aider</p>
                  </div>
                  <a
                    href="tel:+33"
                    className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium px-4 py-2.5 rounded-xl transition-colors whitespace-nowrap"
                  >
                    <Phone size={14} />
                    Nous contacter
                  </a>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Empty state before search */}
          {!searched && !order && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-center py-10"
            >
              <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Package size={28} className="text-slate-400" />
              </div>
              <p className="text-gray-500 text-sm">
                Entrez votre numéro de téléphone pour retrouver votre commande
              </p>
              <div className="mt-6 flex flex-col gap-2 max-w-xs mx-auto text-left">
                {STEPS?.map((step, i) => {
                  const StepIcon = step?.icon;
                  return (
                    <div key={step?.key} className="flex items-center gap-3">
                      <div className={`w-7 h-7 rounded-lg ${step?.bg} ${step?.border} border flex items-center justify-center flex-shrink-0`}>
                        <StepIcon size={13} className={step?.color} />
                      </div>
                      <span className="text-xs text-gray-500">{step?.label}</span>
                      {i < STEPS?.length - 1 && (
                        <ArrowRight size={11} className="text-gray-300 ml-auto" />
                      )}
                    </div>
                  );
                })}
              </div>
            </motion.div>
          )}
        </div>
      </main>
      <UnifiedFooter />
    </div>
  );
}
